/**
 * Types and interfaces for ActivityTable components
 */

import { ScheduleActivity } from '@/types'

export interface Activity {
  id: string
  name: string
  code: string
  early_start_date: string
  early_end_date: string
  late_start_date: string
  late_end_date: string
  target_start_date: string
  target_end_date: string
  actual_start_date: string
  actual_end_date: string
  total_float_hr_cnt: number
  is_critical: boolean
  phys_complete_pct: number
}

export interface ActivityTableProps {
  activities: ScheduleActivity[]
  allWBSNodes?: any[] // All WBS nodes from the project (including parents without activities)
  loading?: boolean
  groupBy?: 'none' | 'wbs'
  udfLabels?: Record<string, string> // Map of udf_type_id -> label (e.g., {'130': 'Crew - RM1&2'})
  onActivityClick?: (activity: ScheduleActivity) => void
  /** Toolbar title (e.g. "Activities"). When set, renders WorkspaceToolbar at top. */
  title?: string
  /** Optional toolbar subtitle (e.g. project name). */
  toolbarSubtitle?: string
  /** If set, scroll position is saved and restored when returning to the page (e.g. "activities.{projectId}"). */
  scrollPersistKey?: string
}

export interface ColumnState {
  key: string | number
  visible: boolean
  order: number
  decimalsEnabled: boolean
  decimalPlaces: number
}

/** Table settings (highlight critical, show time on dates). */
export interface TableSettings {
  highlightCritical: boolean
  showTime: boolean
}

/** Full layout state persisted per project (auto-save and named layouts). */
export interface LayoutState {
  columnState: ColumnState[]
  columnSizing: Record<string, number>
  groupBy: 'none' | 'wbs'
  tableSettings: TableSettings
  floatDisplayUnit: 'hours' | 'days'
}

export interface FilterState {
  dateRange: { start: string; end: string }
  resources: string[]
  wbs: string[]
  calendars: string[]
  types: string[]
  statuses: string[]
  udf: string[]
  criticalOnly: boolean
}

export interface SearchStates {
  wbs: string
  calendars: string
  types: string
  statuses: string
  udf: string
}

export interface ActivityTableRowProps {
  activity: ScheduleActivity
  visibleColumns: any[]
  columnState: ColumnState[]
  showTime: boolean
  floatDisplayUnit: 'hours' | 'days'
  hideExpandIcon?: boolean // Always true - no dropdown functionality
  indentLevel?: number // Indentation level for WBS grouping
}

export interface ColumnSettingsProps {
  columnState: ColumnState[]
  onColumnStateChange: (newState: ColumnState[]) => void
  onClose: () => void
}

export interface FilterPanelProps {
  filters: FilterState
  onFiltersChange: (filters: FilterState) => void
  searchStates: SearchStates
  onSearchStatesChange: (searchStates: SearchStates) => void
  onClose: () => void
  activities: ScheduleActivity[]
  udfLabels?: Record<string, string> // Map of udf_type_id -> label (e.g., {'130': 'Crew - RM1&2'})
}

export interface ActivityTableHeaderProps {
  visibleColumns: any[]
  sortConfig: { key: keyof ScheduleActivity; direction: 'asc' | 'desc' } | null
  onSort: (key: keyof ScheduleActivity) => void
  columnState: ColumnState[]
  onColumnStateChange: (newState: ColumnState[]) => void
}

export const PERCENT_COMPLETE_TYPES = [
  { value: 'phys_complete_pct', label: 'Physical % Complete' },
  { value: 'sch_complete_pct', label: 'Schedule % Complete' },
  { value: 'perf_complete_pct', label: 'Performance % Complete' }
]

export const NUMERIC_KEYS = [
  'total_float_hr_cnt',
  'phys_complete_pct',
  'sch_complete_pct',
  'perf_complete_pct',
  'target_cost',
  'actual_cost',
  'remaining_cost',
  'target_qty',
  'actual_qty',
  'remaining_qty'
]
